/**
 * Ghost Canvas Background Component
 *
 * E012-T013: Background for ghost nodes on canvas.
 * Single Source of Truth for ghost styling constants.
 *
 * Follows the Note-Edgeless-Background pattern:
 * - ShadowlessElement that IS the background (not a container)
 * - Adds CSS class to itself via classList.add()
 * - Applies styles dynamically via Object.assign(this.style, ...)
 */

import { ThemeProvider } from '@blocksuite/affine-shared/services';
import { stopPropagation } from '@blocksuite/affine-shared/utils';
import { SignalWatcher, WithDisposable } from '@blocksuite/global/lit';
import {
  type BlockStdScope,
  PropTypes,
  requiredProperties,
  ShadowlessElement,
  stdContext,
} from '@blocksuite/std';
import { consume } from '@lit/context';
import { computed, effect } from '@preact/signals-core';
import { nothing } from 'lit';
import { property } from 'lit/decorators.js';

import type { GhostBlockModel } from '../schemas/ghost-model.js';
import {
  ghostBackground,
  ghostBackgroundTrash,
} from './ghost-canvas-block.css.js';

// =============================================================================
// Style Constants (aligned with GhostStyleProvider)
// =============================================================================

/**
 * Trash ghost colors - SOLID background, no transparency
 */
const TRASH_COLOR_LIGHT = '#fff1f0'; // Light red background
const TRASH_COLOR_DARK = '#2a1215'; // Dark red background
const TRASH_BORDER_COLOR = '#ff4d4f'; // Red border
const BORDER_RADIUS = '8px';

// =============================================================================
// Component
// =============================================================================

@requiredProperties({
  ghost: PropTypes.instanceOf(Object),
})
export class GhostCanvasBackground extends SignalWatcher(
  WithDisposable(ShadowlessElement)
) {
  readonly backgroundStyle$ = computed(() => {
    const themeProvider = this.std.get(ThemeProvider);
    const theme = themeProvider.theme$.value;
    const isInTrash = this.ghost?.isTargetInTrash ?? false;

    if (isInTrash) {
      // Solid background for trash ghosts - no transparency
      const bgColor = theme === 'dark' ? TRASH_COLOR_DARK : TRASH_COLOR_LIGHT;
      return {
        borderRadius: BORDER_RADIUS,
        backgroundColor: bgColor,
        borderWidth: '2px',
        borderStyle: 'dashed',
        borderColor: TRASH_BORDER_COLOR,
        boxShadow: `0 1px 3px rgba(255, 77, 79, 0.2)`,
      };
    }

    // Use primary background (same as Automation/Database) for ghost appearance
    const backgroundColor = themeProvider.generateColorProperty(
      '--streamlain-background-primary-color',
      '#ffffff',
      theme
    );

    // Ghost-specific style: dashed border to indicate "off-canvas"
    return {
      borderRadius: BORDER_RADIUS,
      backgroundColor: backgroundColor,
      borderWidth: '1.5px',
      borderStyle: 'dashed',
      boxShadow: 'var(--streamlain-shadow-1)',
    };
  });

  override connectedCallback() {
    super.connectedCallback();

    // E018-SPK021: Watch for isTargetInTrash changes via model prop changes
    // We need to poll/subscribe since model props aren't signals
    let lastTrashState = this.ghost?.isTargetInTrash ?? false;

    const updateTrashStyling = () => {
      const currentTrashState = this.ghost?.isTargetInTrash ?? false;

      // Only update if state changed
      if (currentTrashState !== lastTrashState) {
        // Remove old class
        this.classList.remove(ghostBackground, ghostBackgroundTrash);
        // Add new class
        this.classList.add(
          currentTrashState ? ghostBackgroundTrash : ghostBackground
        );
        // Update inline styles
        Object.assign(this.style, this.backgroundStyle$.value);
        lastTrashState = currentTrashState;
      }
    };

    // Initial class
    this.classList.add(lastTrashState ? ghostBackgroundTrash : ghostBackground);

    // Apply styles reactively
    this.disposables.add(
      effect(() => {
        Object.assign(this.style, this.backgroundStyle$.value);
      })
    );

    // Subscribe to model prop changes via std.store.slots.blockUpdated
    const store = this.std?.store;
    if (store?.slots?.blockUpdated) {
      const sub = store.slots.blockUpdated.subscribe(
        (payload: { id: string }) => {
          if (payload.id === this.ghost?.id) {
            updateTrashStyling();
          }
        }
      );
      this.disposables.add(() => sub.unsubscribe());
    }

    this.disposables.addFromEvent(this, 'pointerdown', stopPropagation);
  }

  override render() {
    return nothing;
  }

  @consume({ context: stdContext })
  accessor std!: BlockStdScope;

  @property({ attribute: false })
  accessor editing: boolean = false;

  @property({ attribute: false })
  accessor ghost!: GhostBlockModel;
}

declare global {
  interface HTMLElementTagNameMap {
    'ghost-canvas-background': GhostCanvasBackground;
  }
}
